import React, { useMemo, useState, useEffect } from 'react';
import { ReactComponent as AngleDown } from '../../icons/angle-down.svg';
import { ReactComponent as FilterIcon } from '../../icons/filter.svg';
import './FilterPanel.css';

const defaultFields = [
    [
        { key: 'dateFrom', label: 'Data dodania', type: 'date' },
        { key: 'dateTo', label: 'Data zakończenia', type: 'date' }
    ],
    [
        { key: 'organizerId', label: 'Organizator', type: 'select', optionsKey: 'organizers', placeholder: 'Wybierz' },
        { key: 'locationId', label: 'Lokalizacja', type: 'select', optionsKey: 'locations', placeholder: 'Wybierz' }
    ],
    [
        { key: 'categoryId', label: 'Kategoria', type: 'select', optionsKey: 'categories', placeholder: 'Wybierz' },
        { key: 'status', label: 'Status', type: 'select', optionsKey: 'status', placeholder: 'Wszystkie' }
    ]
];

function FilterPanel({ isOpen, onClose, onApplyFilters, categories = [], fields = null, options = {} }) {
    const [isBasicFiltersExpanded, setIsBasicFiltersExpanded] = useState(true);

    const fieldRows = fields || defaultFields;
    const initialFilters = useMemo(() => {
        const keys = new Set();
        fieldRows.forEach((row) => {
            row.filter(Boolean).forEach((field) => {
                keys.add(field.key);
            });
        });
        const base = {};
        keys.forEach((key) => {
            base[key] = '';
        });
        return base;
    }, [fieldRows]);

    // Filter state
    const [filters, setFilters] = useState(initialFilters);

    useEffect(() => {
        setFilters((prev) => ({ ...initialFilters, ...prev }));
    }, [initialFilters]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleApplyFilters = () => {
        onApplyFilters(filters);
        onClose();
    };

    const handleCancel = () => {
        setFilters(initialFilters);
        onClose();
    };

    const resolvedOptions = useMemo(() => {
        const statusOptions = [
            { value: '', label: 'Wszystkie' },
            { value: 'active', label: 'Aktywne' },
            { value: 'inactive', label: 'Nieaktywne' }
        ];

        const organizerOptions = [
            { value: '', label: 'Wybierz' },
            { value: '1', label: 'Event Masters Sp. z o.o.' },
            { value: '2', label: 'Fundacja Kultury' },
            { value: '3', label: 'Wydawnictwa Polskie' }
        ];

        const locationOptions = [
            { value: '', label: 'Wybierz' },
            { value: 'warszawa', label: 'Warszawa' },
            { value: 'krakow', label: 'Kraków' },
            { value: 'gdansk', label: 'Gdańsk' },
            { value: 'poznan', label: 'Poznań' },
            { value: 'wroclaw', label: 'Wrocław' }
        ];

        const categoriesOptions = [
            { value: '', label: 'Wybierz' },
            ...categories.map((cat) => ({ value: String(cat.id), label: cat.name }))
        ];

        return {
            organizers: organizerOptions,
            locations: locationOptions,
            categories: categoriesOptions,
            status: statusOptions,
            ...options
        };
    }, [categories, options]);

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div className="filter-panel-backdrop" onClick={onClose} />
            
            {/* Filter Panel */}
            <div className="filter-panel">
                {/* Header */}
                <div className="filter-panel-header">
                    <div className="filter-panel-title-row">
                        <h3 className="filter-panel-title">Filtry</h3>
                    </div>
                </div>

                {/* Expandable Section: Basic Filters */}
                <div className="filter-section">
                    <button 
                        className="filter-section-header"
                        onClick={() => setIsBasicFiltersExpanded(!isBasicFiltersExpanded)}
                    >
                        <span className="filter-section-title">Filtry dane podstawowe</span>
                        <AngleDown 
                            className={`filter-section-icon ${isBasicFiltersExpanded ? 'expanded' : ''}`}
                        />
                    </button>

                    {isBasicFiltersExpanded && (
                        <div className="filter-section-content">
                            {fieldRows.map((row, rowIndex) => (
                                <div key={`filter-row-${rowIndex}`} className="filter-row">
                                    {row.filter(Boolean).map((field) => {
                                        if (field.type === 'date') {
                                            return (
                                                <div key={field.key} className="filter-field">
                                                    <label className="filter-label">{field.label}</label>
                                                    <div className="filter-date-input">
                                                        <input
                                                            type="date"
                                                            value={filters[field.key] || ''}
                                                            onChange={(e) => handleFilterChange(field.key, e.target.value)}
                                                            className="filter-input"
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        }

                                        const selectOptions = field.options
                                            || resolvedOptions[field.optionsKey]
                                            || [];
                                        const hasEmpty = selectOptions.some((opt) => opt.value === '');
                                        const placeholder = field.placeholder || 'Wybierz';
                                        const finalOptions = hasEmpty
                                            ? selectOptions
                                            : [{ value: '', label: placeholder }, ...selectOptions];

                                        return (
                                            <div key={field.key} className="filter-field">
                                                <label className="filter-label">{field.label}</label>
                                                <div className="filter-select-wrapper">
                                                    <select
                                                        value={filters[field.key] || ''}
                                                        onChange={(e) => handleFilterChange(field.key, e.target.value)}
                                                        className="filter-select"
                                                    >
                                                        {finalOptions.map((opt) => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <AngleDown className="filter-select-icon" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="filter-panel-actions">
                    <button 
                        className="filter-btn-cancel"
                        onClick={handleCancel}
                    >
                        Anuluj
                    </button>
                    <button 
                        className="filter-btn-apply"
                        onClick={handleApplyFilters}
                    >
                        <FilterIcon className="filter-btn-icon" />
                        Filtruj
                    </button>
                </div>
            </div>
        </>
    );
}

export default FilterPanel;
